// src/hooks/useHasDispensers.ts
//
// Whether the hotels the current user may see have at least one key
// dispenser: the navigation's single source for offering «Dispensadores».
// Read from the screen's own endpoint (`dispensers.total` of the summary
// counts the dispensers registered in scope, whatever the period), asked with
// no scope parameter so the API answers for exactly the user's hotels — it
// scopes the request to them itself; nothing is widened here.
//
// `false` while the answer is loading and when the request fails, like the v2
// gate (useHasV2Property): the entry is never offered on a guess.
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { hasResourcePermission } from '@/config/resourceConfig';
import { hasDispensersInScope } from '@/lib/dispenserView';
import type { DispenserSummary } from '@/types/dispensers';
import { isDocumentationOnly } from '@/features/developers/access';

/** Cache key of the presence check; cleared when the session's user changes. */
export const DISPENSER_PRESENCE_QUERY_KEY = ['insight-v1', 'dispensers', 'presence'] as const;

export const useHasDispensers = (): boolean => {
  const dataProvider = useDataProvider();
  // A role the screen is not for never asks.
  const enabled = hasResourcePermission('dispensers', 'access') && !isDocumentationOnly();
  const { data, isSuccess } = useQuery<DispenserSummary>({
    queryKey: DISPENSER_PRESENCE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const today = new Date().toISOString().slice(0, 10);
      return (await (dataProvider as any).getDispenserSummary({ dateFrom: today, dateTo: today }, signal)).data;
    },
    staleTime: 5 * 60_000,
    enabled,
  });
  return isSuccess && hasDispensersInScope(data);
};

export default useHasDispensers;
